import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export const MODAL_EXIT_MS = 150;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  hideCloseButton?: boolean;
  preventClose?: boolean;
}

const MAX_W: Record<ModalSize, string> = {
  sm: '400px',
  md: '560px',
  lg: '720px',
  xl: '960px',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  preventClose = false,
}: ModalProps) {
  const dialogRef    = useRef<HTMLDivElement>(null);
  const prevFocusRef = useRef<HTMLElement | null>(null);

  const [visible, setVisible] = useState(open);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
    } else {
      setExiting(true);
      const t = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, MODAL_EXIT_MS);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll lock + restore focus
  useEffect(() => {
    if (!open) return;
    prevFocusRef.current = document.activeElement as HTMLElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
      prevFocusRef.current?.focus();
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open || !closeOnEscape || preventClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, preventClose, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const els = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (els.length === 0) { e.preventDefault(); return; }
      const first = els[0]!;
      const last  = els[els.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onTab);
    return () => document.removeEventListener('keydown', onTab);
  }, [open]);

  if (!visible) return null;

  const handleClose = () => { if (!preventClose) onClose(); };
  const hasHeader   = title != null || description != null || !hideCloseButton;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={closeOnBackdrop && !preventClose ? handleClose : undefined}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 800,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(2px)',
          animation: exiting ? 'overlay-exit 150ms ease-out both' : 'overlay-enter 150ms ease both',
        }}
      />

      {/* Centering shell */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 801,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          pointerEvents: 'none',
        }}
      >
        {/* Dialog */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          style={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: MAX_W[size],
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--evo-bg-2)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: 'var(--evo-r-md)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            animation: exiting ? 'modal-out 120ms ease-out both' : 'modal-in 150ms var(--ease-spring) both',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          {hasHeader && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '24px 24px 0',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                {title && (
                  <h2
                    id="modal-title"
                    style={{
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: 600,
                      fontFamily: 'var(--font-display)',
                      color: 'var(--evo-fg-1)',
                      lineHeight: 1.3,
                    }}
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: 'var(--evo-fg-3)',
                      lineHeight: 1.5,
                    }}
                  >
                    {description}
                  </p>
                )}
              </div>
              {!hideCloseButton && (
                <IconButton
                  icon={<Icon name="close" size={14} />}
                  ariaLabel="close"
                  onClick={handleClose}
                  variant="ghost"
                  tone="muted"
                  size="sm"
                />
              )}
            </div>
          )}

          {/* Body */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                padding: '16px 24px',
                borderTop: '1px solid var(--evo-stroke)',
                flexShrink: 0,
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
